/* ==========================================================================
   Admin Product Catalog Page
   Task F23: Product catalog management for pharmacy admins
   ========================================================================== */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search, Plus, Edit2, Copy, Trash2, MoreVertical, RefreshCw, Upload, Download, X, ChevronDown, AlertTriangle, CheckSquare } from 'lucide-react'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Alert } from '@/components/ui/Alert'
import { ProductFormModal } from '@/components/admin/ProductFormModal'
import { CsvCatalogUpload } from '@/components/catalog/CsvCatalogUpload'
import { AdminTableSkeleton } from '@/components/skeletons'
import { useAdminAuth, useAdminPermissions } from '@/hooks/useAdminAuth'
import { useToast } from '@/context/ToastContext'
import { drugCodeLabel } from '@/utils/countries'
import {
  listCatalog,
  listCategories,
  createCategory,
  createProduct,
  updateProduct,
  bulkDeleteProducts,
  downloadCatalogCsvTemplate,
  exportCatalogCsv,
  importCatalogCsv,
} from '@/api/admin'
import { ApiClientError } from '@/api/client'
import type {
  CatalogProductResponse,
  CatalogProductCreate,
  CatalogProductUpdate,
  Category,
  CategoryBrief,
} from '@/api/types'

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const ITEMS_PER_PAGE = 10

/* --------------------------------------------------------------------------
   Status Badge Component
   -------------------------------------------------------------------------- */

function StatusBadge({ isActive }: { isActive: boolean }) {
  if (isActive) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Active
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
      Inactive
    </span>
  )
}

/* --------------------------------------------------------------------------
   Pagination Component
   -------------------------------------------------------------------------- */

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = []
    const showPages = 5 // Max pages to show

    if (totalPages <= showPages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Show first, last, and pages around current
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i)
        pages.push('ellipsis')
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1)
        pages.push('ellipsis')
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('ellipsis')
        pages.push(currentPage - 1)
        pages.push(currentPage)
        pages.push(currentPage + 1)
        pages.push('ellipsis')
        pages.push(totalPages)
      }
    }

    return pages
  }

  return (
    <div className="flex items-center justify-center gap-1 sm:gap-2">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1.5 text-sm text-charcoal hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        aria-label="Previous page"
      >
        ←
      </button>

      {getPageNumbers().map((page, index) =>
        page === 'ellipsis' ? (
          <span key={`ellipsis-${index}`} className="px-2 py-1 text-gray-500">
            ...
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              page === currentPage
                ? 'bg-deep-purple text-white'
                : 'text-charcoal hover:bg-gray-100'
            }`}
            aria-label={`Page ${page}`}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        )
      )}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1.5 text-sm text-charcoal hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        aria-label="Next page"
      >
        →
      </button>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Categories Cell — truncates long lists with an expand/collapse arrow
   -------------------------------------------------------------------------- */

function CategoriesCell({ names }: { names: string[] }) {
  const [expanded, setExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const textRef = useRef<HTMLSpanElement>(null)

  const text = names.join(', ')

  // Show the arrow only when the collapsed text actually overflows
  useEffect(() => {
    const el = textRef.current
    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth)
    }
  }, [text])

  if (names.length === 0) return <>—</>

  return (
    <div className="flex items-start gap-1 max-w-[14rem]">
      <span
        ref={textRef}
        className={expanded ? 'whitespace-normal break-words' : 'truncate'}
      >
        {text}
      </span>
      {(isOverflowing || expanded) && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="shrink-0 p-0.5 mt-0.5 rounded text-gray-400 hover:text-deep-purple hover:bg-gray-100 transition-colors"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse categories' : 'Show all categories'}
        >
          <ChevronDown
            className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
      )}
    </div>
  )
}

/* --------------------------------------------------------------------------
   Empty State Component
   -------------------------------------------------------------------------- */

function EmptyState({
  onAddProduct,
  canAdd = true,
}: {
  onAddProduct: () => void
  canAdd?: boolean
}) {
  return (
    <div className="text-center py-12">
      <div className="mx-auto w-16 h-16 bg-soft-lavender rounded-full flex items-center justify-center mb-4">
        <Plus className="w-8 h-8 text-deep-purple" />
      </div>
      <h3 className="text-lg font-semibold text-charcoal mb-2">
        No products yet
      </h3>
      <p className="text-gray-600 mb-6 max-w-sm mx-auto">
        {canAdd
          ? 'Start building your catalog by adding your first product. Products will be available for providers to prescribe.'
          : 'No products have been added to this catalog yet.'}
      </p>
      {canAdd && (
        <Button variant="primary" onClick={onAddProduct}>
          <Plus className="w-4 h-4 mr-2" />
          Add Your First Product
        </Button>
      )}
    </div>
  )
}


/* --------------------------------------------------------------------------
   Main Component
   -------------------------------------------------------------------------- */

export function Catalog() {
  const { pharmacy } = useAdminAuth()
  // Staff (PORTAL-94) can view the formulary but not modify it — hide write CTAs.
  const { canManage } = useAdminPermissions()
  const { success: showSuccess, error: showError } = useToast()
  const slug = pharmacy?.portal_slug || ''

  // Data state
  const [products, setProducts] = useState<CatalogProductResponse[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [showInactive, setShowInactive] = useState(true)

  // Category filter via URL (?category=<id>) — linked from the Categories page
  const [searchParams, setSearchParams] = useSearchParams()
  const categoryFilterId = searchParams.get('category')
  const categoryFilterName = useMemo(() => {
    if (!categoryFilterId) return null
    const fromList = categories.find((c) => c.id === categoryFilterId)?.name
    if (fromList) return fromList
    for (const p of products) {
      const match = p.categories.find((c) => c.id === categoryFilterId)
      if (match) return match.name
    }
    return null
  }, [categoryFilterId, categories, products])

  function clearCategoryFilter() {
    searchParams.delete('category')
    setSearchParams(searchParams, { replace: true })
  }

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)

  // Modal state
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<CatalogProductResponse | null>(null)
  const [duplicatingProduct, setDuplicatingProduct] = useState<CatalogProductResponse | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // CSV upload state
  const [showCsvUpload, setShowCsvUpload] = useState(false)
  const [applyEdits, setApplyEdits] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Multi-select state (PORTAL-127)
  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Delete confirmation state (one product from the row menu, or the selection)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [productsToDelete, setProductsToDelete] = useState<CatalogProductResponse[]>([])
  const [isDeleting, setIsDeleting] = useState(false)

  // Fetch products
  const fetchProducts = useCallback(async () => {
    if (!slug) return

    setLoading(true)
    setError(null)

    try {
      const response = await listCatalog(slug)
      setProducts(response.products)
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to load products. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [slug])

  // Fetch categories for the product form's multi-select (PORTAL-77).
  // Non-fatal on failure: keep whatever list we already have rather than
  // blanking the form's options on a transient error. Categories are
  // pharmacy-scoped, so the list resets when the slug changes and late
  // responses for a previous pharmacy are discarded.
  const slugRef = useRef(slug)
  useEffect(() => {
    slugRef.current = slug
    setCategories([])
  }, [slug])

  // Monotonic id so overlapping fetches (initial load vs post-import
  // refresh) can't apply out of order — only the latest request wins.
  const categoriesRequestIdRef = useRef(0)

  const fetchCategories = useCallback(async () => {
    if (!slug) return
    const requestId = ++categoriesRequestIdRef.current
    try {
      const response = await listCategories(slug)
      if (
        slugRef.current === slug &&
        categoriesRequestIdRef.current === requestId
      ) {
        setCategories(response.categories)
      }
    } catch {
      // keep previous categories for this pharmacy
    }
  }, [slug])

  useEffect(() => {
    fetchProducts()
    fetchCategories()
  }, [fetchProducts, fetchCategories])

  // Inline category creation from the product form
  async function handleCreateCategory(name: string): Promise<CategoryBrief | null> {
    try {
      const created = await createCategory(slug, name)
      showSuccess(`Category "${created.name}" created`)
      await fetchCategories()
      return created
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to create category. Please try again.'
      showError(message)
      return null
    }
  }

  // Filter and search products
  const filteredProducts = useMemo(() => {
    let result = products

    // Filter by category (from the Categories page link)
    if (categoryFilterId) {
      result = result.filter((p) =>
        p.categories.some((c) => c.id === categoryFilterId)
      )
    }

    // Filter by active status
    if (!showInactive) {
      result = result.filter((p) => p.is_active)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase()
      result = result.filter(
        (p) =>
          p.display_name.toLowerCase().includes(query) ||
          p.product_code.toLowerCase().includes(query) ||
          (p.ndc && p.ndc.toLowerCase().includes(query)) ||
          (p.strength && p.strength.toLowerCase().includes(query)) ||
          p.categories.some((c) => c.name.toLowerCase().includes(query))
      )
    }

    return result
  }, [products, searchQuery, showInactive, categoryFilterId])

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE)
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredProducts.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredProducts, currentPage])

  // The selection as it applies to the current list. Ids that dropped out
  // after a refetch (deleted elsewhere, deactivated by an import) are ignored,
  // so the count shown always matches what "Delete selected" sends.
  const selectedProducts = useMemo(
    () => filteredProducts.filter((p) => selectedIds.has(p.id)),
    [filteredProducts, selectedIds]
  )

  // Reset to page 1 when search/filter changes. The selection is cleared too,
  // so products hidden by the new filter can never be deleted.
  useEffect(() => {
    setCurrentPage(1)
    setSelectedIds(new Set())
  }, [searchQuery, showInactive, categoryFilterId])

  // Open add modal
  function handleAddProduct() {
    setEditingProduct(null)
    setDuplicatingProduct(null)
    setIsFormModalOpen(true)
  }

  // Open edit modal
  function handleEditProduct(product: CatalogProductResponse) {
    setEditingProduct(product)
    setDuplicatingProduct(null)
    setIsFormModalOpen(true)
  }

  // Open duplicate modal (create mode, pre-filled from an existing product)
  function handleDuplicateProduct(product: CatalogProductResponse) {
    setEditingProduct(null)
    setDuplicatingProduct(product)
    setIsFormModalOpen(true)
  }

  // Close form modal
  function handleCloseFormModal() {
    setIsFormModalOpen(false)
    setEditingProduct(null)
    setDuplicatingProduct(null)
  }

  // Handle form submission
  async function handleFormSubmit(data: CatalogProductCreate | CatalogProductUpdate) {
    setIsSubmitting(true)

    try {
      if (editingProduct) {
        // Update existing product
        await updateProduct(slug, editingProduct.id, data as CatalogProductUpdate)
        showSuccess('Product updated successfully')
      } else {
        // Create new product (also covers duplication, which is a fresh create)
        await createProduct(slug, data as CatalogProductCreate)
        showSuccess(
          duplicatingProduct ? 'Product duplicated successfully' : 'Product added successfully'
        )
      }

      handleCloseFormModal()
      await fetchProducts()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to save product. Please try again.'
      showError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Download the current catalog as an editable CSV (PORTAL-58)
  async function handleExportCatalog() {
    setExporting(true)
    try {
      const blob = await exportCatalogCsv(slug)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'catalog_export.csv'
      link.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to export catalog. Please try again.'
      showError(message)
    } finally {
      setExporting(false)
    }
  }

  // Select mode: toggle checkboxes (PORTAL-127)
  function exitSelectMode() {
    setSelectMode(false)
    setSelectedIds(new Set())
  }

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // "Select all" picks every product matching the current search/filters,
  // not just the current page
  function handleSelectAll() {
    setSelectedIds(new Set(filteredProducts.map((p) => p.id)))
  }

  // Open delete confirmation
  function openDeleteConfirm(toDelete: CatalogProductResponse[]) {
    setProductsToDelete(toDelete)
    setDeleteConfirmOpen(true)
  }

  function handleDeleteClick(product: CatalogProductResponse) {
    openDeleteConfirm([product])
  }

  function handleDeleteSelected() {
    openDeleteConfirm(selectedProducts)
  }

  // Confirm permanent delete
  async function handleConfirmDelete() {
    if (productsToDelete.length === 0) return

    setIsDeleting(true)

    try {
      await bulkDeleteProducts(slug, productsToDelete.map((p) => p.id))
      showSuccess(
        productsToDelete.length === 1
          ? 'Product deleted permanently'
          : `${productsToDelete.length} products deleted permanently`
      )
      setDeleteConfirmOpen(false)
      exitSelectMode()
      await fetchProducts()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to delete product. Please try again.'
      showError(message)
      // A 404 usually means another admin deleted one of these products.
      // Refetch so it drops out of the list and the selection, instead of
      // every retry failing the same way.
      setDeleteConfirmOpen(false)
      await fetchProducts()
    } finally {
      setIsDeleting(false)
    }
  }

  // Toggle product active status
  async function handleToggleActive(product: CatalogProductResponse) {
    try {
      await updateProduct(slug, product.id, { is_active: !product.is_active })
      showSuccess(
        product.is_active
          ? 'Product deactivated successfully'
          : 'Product activated successfully'
      )
      await fetchProducts()
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to update product status. Please try again.'
      showError(message)
    }
  }

  return (
    <div className="p-6 sm:p-8">
      <div className="max-w-6xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl text-charcoal">Product Catalog</h1>
            <p className="text-charcoal/70 mt-1">
              Manage the products available for providers to prescribe
            </p>
          </div>
          {canManage && (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setShowCsvUpload(!showCsvUpload)}>
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
              <Button variant="primary" onClick={handleAddProduct}>
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </div>
          )}
        </div>

        {/* CSV Import / Bulk Edit Section */}
        {showCsvUpload && (
          <Card className="p-4 sm:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
              <div>
                <h3 className="text-sm font-semibold text-charcoal mb-1">
                  Import or Bulk Edit Products via CSV
                </h3>
                <p className="text-xs text-charcoal/60">
                  Import new products from the template, or download the current
                  catalog, edit it in a spreadsheet, and re-upload with
                  &ldquo;Update existing products&rdquo; checked to apply your
                  edits.
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExportCatalog}
                loading={exporting}
                className="whitespace-nowrap"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Catalog
              </Button>
            </div>
            <label className="flex items-center gap-2 text-sm text-charcoal cursor-pointer mb-3">
              <input
                type="checkbox"
                checked={applyEdits}
                onChange={(e) => setApplyEdits(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-deep-purple focus:ring-lavender"
              />
              Update existing products (rows matching a product code apply as
              edits instead of being flagged as duplicates)
            </label>
            {applyEdits && (
              <div className="flex items-start gap-2 p-3 mb-3 bg-amber-50 border border-amber-200 rounded-md">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900">
                  In edit mode the file is the source of truth:{' '}
                  <span className="font-medium">
                    an empty cell clears that field on the matched product.
                  </span>{' '}
                  Start from &ldquo;Download Catalog&rdquo; so you edit current
                  values — you&apos;ll review every change, including clears,
                  before anything is applied.
                </p>
              </div>
            )}
            <CsvCatalogUpload
              onImport={(file) =>
                importCatalogCsv(slug, file, applyEdits ? 'upsert' : 'create')
              }
              onPreview={
                applyEdits
                  ? (file) => importCatalogCsv(slug, file, 'upsert', true)
                  : undefined
              }
              onDownloadTemplate={() => downloadCatalogCsvTemplate(slug)}
              onSuccess={() => {
                // Imports can create categories too — refresh both lists
                void Promise.all([fetchProducts(), fetchCategories()])
              }}
            />
          </Card>
        )}

        {/* Error Alert */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchProducts}
              className="ml-4"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry
            </Button>
          </Alert>
        )}

        {/* Main Content */}
        <Card className="overflow-visible">
          {loading ? (
            <AdminTableSkeleton columns={6} rows={5} />
          ) : products.length === 0 ? (
            <EmptyState onAddProduct={handleAddProduct} canAdd={canManage} />
          ) : (
            <>
              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                {/* Search Input */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Show Inactive Toggle */}
                <label className="flex items-center gap-2 text-sm text-charcoal cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-deep-purple focus:ring-lavender"
                  />
                  Show inactive
                </label>

                {canManage && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
                    className="whitespace-nowrap"
                  >
                    {selectMode ? (
                      'Cancel'
                    ) : (
                      <>
                        <CheckSquare className="w-4 h-4 mr-2" />
                        Select
                      </>
                    )}
                  </Button>
                )}
              </div>

              {/* Selection bar (PORTAL-127) */}
              {selectMode && (
                <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-soft-lavender rounded-md text-sm">
                  <span className="font-medium text-charcoal">
                    {selectedProducts.length} selected
                  </span>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-deep-purple hover:underline"
                  >
                    Select all ({filteredProducts.length})
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set())}
                    className="text-deep-purple hover:underline"
                  >
                    Deselect all
                  </button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDeleteSelected}
                    disabled={selectedProducts.length === 0}
                    className="sm:ml-auto"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete selected
                  </Button>
                </div>
              )}

              {/* Active category filter chip */}
              {categoryFilterId && (
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-gray-600">Filtered by category:</span>
                  <span className="inline-flex items-center gap-1.5 bg-soft-lavender text-deep-purple text-sm font-medium px-3 py-1 rounded-full">
                    {categoryFilterName || 'Unknown category'}
                    <button
                      onClick={clearCategoryFilter}
                      className="hover:text-charcoal transition-colors"
                      aria-label="Clear category filter"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                </div>
              )}

              {/* Results Summary */}
              <div className="text-sm text-gray-600 mb-4">
                Showing {paginatedProducts.length} of {filteredProducts.length} products
                {searchQuery && ` matching "${searchQuery}"`}
                {categoryFilterName && ` in "${categoryFilterName}"`}
              </div>

              {/* Products Table */}
              {filteredProducts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No products found matching your search.
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden sm:block">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          {selectMode && <th className="w-8 pb-3" aria-label="Select" />}
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Product
                          </th>
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Code
                          </th>
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            {drugCodeLabel(pharmacy?.country)}
                          </th>
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Categories
                          </th>
                          <th className="text-left text-sm font-medium text-gray-600 pb-3">
                            Status
                          </th>
                          <th className="text-right text-sm font-medium text-gray-600 pb-3">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedProducts.map((product) => (
                          <tr
                            key={product.id}
                            className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                          >
                            {selectMode && (
                              <td className="py-4 pr-2">
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(product.id)}
                                  onChange={() => toggleSelected(product.id)}
                                  aria-label={`Select ${product.display_name}`}
                                  className="w-4 h-4 rounded border-gray-300 text-deep-purple focus:ring-lavender"
                                />
                              </td>
                            )}
                            <td className="py-4 pr-4">
                              <div className="text-charcoal font-medium">
                                {product.display_name}
                                {product.strength && (
                                  <span className="ml-2 text-sm font-normal text-gray-500">
                                    {product.strength}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500 mt-0.5 line-clamp-1">
                                {product.dose_specific_directions || '—'}
                              </div>
                            </td>
                            <td className="py-4 pr-4">
                              <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">
                                {product.product_code}
                              </code>
                            </td>
                            <td className="py-4 pr-4 text-gray-600">
                              {product.ndc || '—'}
                            </td>
                            <td className="py-4 pr-4 text-gray-600">
                              <CategoriesCell
                                names={product.categories.map((c) => c.name)}
                              />
                            </td>
                            <td className="py-4 pr-4">
                              <StatusBadge isActive={product.is_active} />
                            </td>
                            <td className="py-4 text-right">
                              {canManage && !selectMode && (
                              <Menu as="div" className="relative inline-block text-left">
                                <MenuButton
                                  aria-label={`Actions for ${product.display_name}`}
                                  className="p-2 rounded-md hover:bg-gray-100 transition-colors"
                                >
                                  <MoreVertical className="w-4 h-4 text-gray-500" />
                                </MenuButton>
                                <MenuItems
                                  anchor="bottom end"
                                  className="z-50 w-40 [--anchor-gap:4px] bg-white rounded-md shadow-lg ring-1 ring-black/5 focus:outline-none"
                                >
                                  <div className="py-1">
                                    <MenuItem>
                                      {({ active }) => (
                                        <button
                                          onClick={() => handleEditProduct(product)}
                                          className={`flex items-center w-full px-4 py-2 text-sm ${
                                            active ? 'bg-gray-100' : ''
                                          } text-charcoal`}
                                        >
                                          <Edit2 className="w-4 h-4 mr-2" />
                                          Edit
                                        </button>
                                      )}
                                    </MenuItem>
                                    <MenuItem>
                                      {({ active }) => (
                                        <button
                                          onClick={() => handleDuplicateProduct(product)}
                                          className={`flex items-center w-full px-4 py-2 text-sm ${
                                            active ? 'bg-gray-100' : ''
                                          } text-charcoal`}
                                        >
                                          <Copy className="w-4 h-4 mr-2" />
                                          Duplicate
                                        </button>
                                      )}
                                    </MenuItem>
                                    <MenuItem>
                                      {({ active }) => (
                                        <button
                                          onClick={() => handleToggleActive(product)}
                                          className={`flex items-center w-full px-4 py-2 text-sm ${
                                            active ? 'bg-gray-100' : ''
                                          } text-charcoal`}
                                        >
                                          <RefreshCw className="w-4 h-4 mr-2" />
                                          {product.is_active ? 'Deactivate' : 'Activate'}
                                        </button>
                                      )}
                                    </MenuItem>
                                    <MenuItem>
                                      {({ active }) => (
                                        <button
                                          onClick={() => handleDeleteClick(product)}
                                          className={`flex items-center w-full px-4 py-2 text-sm ${
                                            active ? 'bg-red-50' : ''
                                          } text-error`}
                                        >
                                          <Trash2 className="w-4 h-4 mr-2" />
                                          Delete
                                        </button>
                                      )}
                                    </MenuItem>
                                  </div>
                                </MenuItems>
                              </Menu>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="sm:hidden space-y-4">
                    {paginatedProducts.map((product) => (
                      <div
                        key={product.id}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          {selectMode && (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(product.id)}
                              onChange={() => toggleSelected(product.id)}
                              aria-label={`Select ${product.display_name}`}
                              className="w-5 h-5 mt-0.5 shrink-0 rounded border-gray-300 text-deep-purple focus:ring-lavender"
                            />
                          )}
                          <div className="flex-1 font-medium text-charcoal">
                            {product.display_name}
                            {product.strength && (
                              <span className="ml-2 text-sm font-normal text-gray-500">
                                {product.strength}
                              </span>
                            )}
                          </div>
                          <StatusBadge isActive={product.is_active} />
                        </div>
                        <div className="text-sm text-gray-500 mb-3 line-clamp-2">
                          {product.dose_specific_directions || '—'}
                        </div>
                        <div className="flex flex-wrap gap-2 text-sm text-gray-600 mb-3">
                          <span className="bg-gray-100 px-2 py-0.5 rounded">
                            {product.product_code}
                          </span>
                          {product.categories.map((category) => (
                            <span
                              key={category.id}
                              className="bg-gray-100 px-2 py-0.5 rounded"
                            >
                              {category.name}
                            </span>
                          ))}
                        </div>
                        {canManage && !selectMode && (
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleEditProduct(product)}
                          >
                            <Edit2 className="w-3.5 h-3.5 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDuplicateProduct(product)}
                          >
                            <Copy className="w-3.5 h-3.5 mr-1" />
                            Duplicate
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(product)}
                          >
                            {product.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                        </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Product Form Modal */}
      <ProductFormModal
        open={isFormModalOpen}
        onClose={handleCloseFormModal}
        onSubmit={handleFormSubmit}
        product={editingProduct}
        duplicateFrom={duplicatingProduct}
        loading={isSubmitting}
        categories={categories}
        onCreateCategory={handleCreateCategory}
        country={pharmacy?.country}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title={
          productsToDelete.length === 1
            ? 'Delete Product'
            : `Delete ${productsToDelete.length} Products`
        }
        size="sm"
      >
        {productsToDelete.length === 1 ? (
          <p className="text-gray-600 mb-6">
            Are you sure you want to permanently delete{' '}
            <strong className="text-charcoal">
              {productsToDelete[0].display_name}
            </strong>
            ? This action cannot be undone. Existing prescriptions will be preserved
            but will no longer reference this product.
          </p>
        ) : (
          <>
            <p className="text-gray-600 mb-4">
              Are you sure you want to permanently delete these{' '}
              {productsToDelete.length} products? This action cannot be undone.
              Existing prescriptions will be preserved but will no longer
              reference these products.
            </p>
            <ul className="max-h-48 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100 text-sm mb-6">
              {productsToDelete.map((product) => (
                <li key={product.id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="text-charcoal">
                    {product.display_name}
                    {product.strength && (
                      <span className="ml-1 text-gray-500">{product.strength}</span>
                    )}
                  </span>
                  <code className="shrink-0 whitespace-nowrap text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                    {product.product_code}
                  </code>
                </li>
              ))}
            </ul>
          </>
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="secondary"
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeleting}
            className="sm:order-1"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmDelete}
            loading={isDeleting}
            className="sm:order-2 sm:ml-auto"
          >
            Delete Permanently
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default Catalog
